import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Component, inject} from '@angular/core';
import { FormsModule , ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { AngularEditorModule, AngularEditorConfig } from '@kolkov/angular-editor';
import { ConstantService } from 'src/app/services/common/constant.service';
import { HasPermissionDirective } from 'src/app/services/common/has-permission.directive';
import { MarketingEmailModel, UsersService } from 'src/app/services/user';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { EditorComponent, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';

@Component({
  selector: 'app-marketing-email',
  templateUrl: './marketing-email.component.html',
  imports: [TranslateModule, NgSelectModule, AngularEditorModule, FormsModule, ReactiveFormsModule, HasPermissionDirective, EditorComponent],
  providers: [
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' }
  ]
})
export class MarketingEmailComponent {
  private readonly constantService = inject(ConstantService);
  private readonly fb = inject(FormBuilder);
  private readonly ngbModal = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private readonly userService = inject(UsersService);
  
  sendEmailsForm: FormGroup;
  angularEditorConfig: AngularEditorConfig;
  htmlContent: string;
  emailBody: string = '';
  types = ["Rich Text","HTML"];
  init: EditorComponent['init'] = {
    base_url: '/tinymce',
    suffix: '.min',
    height: 800,
    menubar: true, 
    branding: false,
    statusbar: false,
    promotion: false,
    plugins: 'link lists',
    toolbar: 'bold italic bullist numlist | alignleft aligncenter alignright | link ',
    menu: {
      view: { title: 'View', items: ''},
    },
    setup: (editor: any) => {
      editor.on('keyup change', () => {
        this.htmlContent = editor.getContent();
      });
    }
  };

  constructor(){
    this.angularEditorConfig = this.constantService.editorConfig;
    this.sendEmailsForm = this.fb.group({
      subject: [null, Validators.compose([Validators.required])],
      type: ["Rich Text", Validators.compose([Validators.required])],
      body: [null, Validators.compose([Validators.required])]
    });
  }

  bodyChange() {
    this.htmlContent = this.sendEmailsForm.controls.body.value;
  }

  private async showDangerWarning() {
    const modalRef = this.ngbModal.open(ConfirmDialogComponent, {
      size: 'md',
      windowClass: 'fadeInUp animated huge',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.title = "Warning";
    modalRef.componentInstance.message = "This action will send the email to all organisations and cannot be undone. Are you sure you want to proceed?";
    modalRef.componentInstance.type = 'danger';
    return await modalRef.result;
  }

  private async showSentConfirmation() {
    const modalRef = this.ngbModal.open(ConfirmDialogComponent, {
      size: 'md',
      windowClass: 'fadeInUp animated huge',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.title = "Completed";
    modalRef.componentInstance.message = "Bulk emails were sent successfully. Click ‘Yes’ to return to the home page.";
    return await modalRef.result;
  }

  async send(){
    if(!await this.showDangerWarning()) return;

    const marketingEmailModel: MarketingEmailModel = {
      subject: this.sendEmailsForm.controls.subject.value,
      body: this.sendEmailsForm.controls.body.value
    }

    try {
      await firstValueFrom(this.userService.apiUsersSendMarketingEmailsPut(marketingEmailModel));
      this.toastrService.success(this.translateService.instant('Action.Success'));
      if(await this.showSentConfirmation()){
        this.router.navigate(['dashboard']);
      }
    } catch (error) {
      this.toastrService.error(this.translateService.instant('Action.Error'));
    }
  }
}
